// @flow
import * as React from "react";
import {reisy, use} from "reisy";
import {injectIntl, intlShape, defineMessages} from "react-intl";

import Link from "./link.js";
import Image, {chooseVariant} from "./image.js";
import InlineLoader from './inline-loader.js';
import Heading from './heading';

const messages = defineMessages({
	prev: {
		id: "prevnext.prev",
		defaultMessage: "previous story",
	},
	next: {
		id: "prevnext.next",
		defaultMessage: "next story",
	},
});

export const styles = reisy`
@namespace PrevNext;

Height: 10em;
Width: 15em;

TargetTop {
	top: 0;
	bottom: auto;
	height: $Height;
	max-height: 50vh;
	flex-shrink: 0;
	display: inline-block;
}
TargetRight {
	right: 0;
	left: auto;
	width: $Width;
	max-width: 50vw;
	flex-shrink: 0;
	display: inline-block;
}
TargetBottom {
	bottom: 0;
	top: auto;
	height: $Height;
	max-height: 50vh;
	flex-shrink: 0;
	display: inline-block;
}
TargetLeft {
	left: 0;
	right: auto;
	width: $Width;
	max-width: 50vw;
	flex-shrink: 0;
	display: inline-block;
}

Container {
	@extends Helpers.AbsFull;
	text-decoration: none;
	background: #222;
	color: inherit;
	display: flex;
	align-items: center;
}
Horizontal {
	@extends Container;
}
Vertical {
	@extends Container;
	flex-direction: column;
}

/* if you wonder why we need a separate element at all, its because the
 * background should be faded a bit (opacity) */
Background {
	@extends Helpers.AbsFull;
	background-size: cover;
	background-position: center center;
	opacity: 0.25;
}

Drawer {
	z-index: 1;
	max-width: 75%;
	background: white;
	text-decoration: none;
	padding: 0.5em 1em;
	text-align: center;
	position: relative;
}
DrawerTop {
	padding-top: 1em;
}
DrawerRight {
	margin-left: auto;
	text-align: right;
}
DrawerBottom {
	margin-top: auto;
	padding-bottom: 1em;
}
DrawerLeft {
	text-align: left;
}
Direction {
	display: block;
	font-family: $Fonts.aside;
	font-size: 0.8125em;
	color: #333;
	letter-spacing: 0.035em;
	margin-bottom: 0.6154em;
}
CollectionTitle {
	margin: 0;
	font-family: $Fonts.subtitle;
	font-size: 1em;
	line-height: 1em;
	opacity: 0.6;
	font-weight: normal;
}
Title {
	margin: 0;
	font-family: $Fonts.title;
	font-size: 1.2em;
	line-height: 1em;
	font-weight: normal;
}

TagWrapper {
	display: flex;
	justify-content: space-between;
	padding: 24px;
	flex-shrink: 0;
	flex-grow: 0;

	$Media.smaller {
		align-items: center;
		flex-direction: column-reverse;
		padding: 24px 24px 12px 24px;
	}

	@media print {
		display: none;
	}
}

Tag {
	display: flex;
	width: 100%;
	max-width: 400px;
	color: $Colors.TextDark;
	background: #fff;
	align-items: center;
	text-decoration: none;
	box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2);
	box-sizing: border-box;

	$Media.smaller {
		margin-bottom: 12px;
	}
}
TagImageWrapper {
	width: 100px;
	height: 100px;
	flex-shrink: 0;
	flex-grow: 0;
	position: relative;
	font-size: .75em;
}
TagImage {
	width: 100%;
	height: 100%;
}
TagLabel {
	padding: 10px 20px 10px 10px;
	hyphens: auto;
}
TagLabelnext {
	@extends TagLabel;

	order: -1;
	text-align: right;
	padding: 10px 10px 10px 20px;
}

`;

type Props = {
	story: any;
	href: string;
	vertical: boolean;
	direction: "prev" | "next";
	position: "Top" | "Right" | "Bottom" | "Left";
	intl: any; // TODO
};

export const Drawer = injectIntl(({parent, story, href, vertical, direction, position, intl}: Props) => (
	<Link
		{...use(vertical ? styles.Vertical : styles.Horizontal, styles[`Target${position}`])}
		to={href}>
		<div
			{...use(styles.Background, {
				// we explicitly want a lowres blurred out version of the cover here
				backgroundImage: `url("${chooseVariant(story.image, {width: 1}).url}")`,
			})} />
		<div {...use(styles.Drawer, styles[`Drawer${position}`])}>
			<span {...use(styles.Direction)}>{intl.formatMessage(messages[direction])}</span>
			{parent && <strong {...use(styles.CollectionTitle)}>{parent.title}</strong>}
			<Heading lvl={3} {...use(styles.Title)}>{story.title}</Heading>
		</div>
	</Link>
));


export class Tag extends React.Component {
	constructor (props) {
		super(props);

		this.state = {
			loading: false
		};
	}

	componentWillReceiveProps(nextProps) {
		this.setState({ loading: false });
	}

	onClick = e => {
		this.setState({loading: true});
	}

	render() {
		const {parent, story, href, direction, loading} = this.props;
		const {intl} = this.context;

		return (<Link to={href} {...use(styles.Tag)} onClick={this.onClick}>
			<div {...use(styles.TagImageWrapper)}>
				<Image
					style={styles.TagImage}
					image={story.image}
					sizes={'100px'}
					availWidth={100}
					ratio={1}
					cover lazy placeholder />
				{this.state && this.state.loading ? <InlineLoader key="loading" /> : null}
			</div>
			<div {...use(styles.TagLabel, styles[`TagLabel${direction}`])}>
				<span {...use(styles.Direction)}>{intl.formatMessage(messages[direction])}</span>
				{parent && <strong {...use(styles.CollectionTitle)}>{parent.title}</strong>}
				<Heading lvl={3} {...use(styles.Title)}>{story.title}</Heading>
			</div>
		</Link>);
	}
}
Tag.contextTypes = {
	intl: intlShape,
};

export const TagWrapper = ({style, children}) => (
	<div {...use(styles.TagWrapper, style)}>
		{children}
	</div>
)

export const Target = ({href, position}: Props) => (
	<Link {...use(styles[`Target${position}`])} to={href} />
);


// WEBPACK FOOTER //
// ./lib/components/prevnext.js